import { Close as CloseIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  SxProps,
  Theme,
  Divider,
  CircularProgress,
} from "@mui/material";
import React, { ReactNode } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

export interface PopupButton {
  label: string;
  onClick: () => void;
  variant?: "contained" | "outlined" | "text";
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
  disabled?: boolean;
  loading?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  sx?: SxProps<Theme>;
}

export interface CommonPopupProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  iconColor?: string;
  iconBackgroundColor?: string;
  children: ReactNode;
  buttons?: PopupButton[];
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  fullWidth?: boolean;
  showCloseButton?: boolean;
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
  contentSx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  actionsSx?: SxProps<Theme>;
  paperSx?: SxProps<Theme>;
  minHeight?: number;
  loading?: boolean;
}

const CommonPopup: React.FC<CommonPopupProps> = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  iconColor,
  iconBackgroundColor,
  children,
  buttons = [],
  maxWidth = "sm",
  minHeight = 400,
  fullWidth = true,
  showCloseButton = true,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  contentSx,
  titleSx,
  actionsSx,
  paperSx,
  loading = false,
}) => {
  const { colors } = useCelleraTheme();

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const defaultPaperSx: SxProps<Theme> = {
    borderRadius: "8px",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
    margin: "16px",
    minHeight,
    ...paperSx,
  };

  const defaultTitleSx: SxProps<Theme> = {
    pb: 1,
    ...titleSx,
  };

  const defaultContentSx: SxProps<Theme> = {
    pt: 2,
    ...contentSx,
  };

  const defaultActionsSx: SxProps<Theme> = {
    gap: 3,
    justifyContent: "flex-end",
    display: "flex",
    ...actionsSx,
  };

  return (
    <Dialog
      open={open}
      onClose={disableBackdropClick ? undefined : handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      disableEscapeKeyDown={disableEscapeKeyDown}
      slotProps={{
        paper: {
          sx: defaultPaperSx,
        },
      }}
    >
      <DialogTitle sx={defaultTitleSx}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "3",
            }}
          >
            {icon && (
              <Box
                sx={{
                  width: "10",
                  height: "10",
                  borderRadius: "full",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: iconBackgroundColor || colors.primary.light,
                  color: iconColor || colors.primary.main,
                }}
              >
                {icon}
              </Box>
            )}
            <Box>
              <Typography
                variant="h5"
                className="font-semibold"
                sx={{ color: colors.text.primary }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: "14px",
                    mt: 0.5,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          {showCloseButton && (
            <IconButton
              onClick={handleClose}
              disabled={loading}
              size="small"
              sx={{
                color: colors.text.secondary,
                "&:hover": {
                  backgroundColor: colors.background.secondary,
                  color: colors.text.primary,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <Divider />

      <DialogContent sx={defaultContentSx}>{children}</DialogContent>

      <Divider />

      {buttons.length > 0 && (
        <DialogActions sx={defaultActionsSx}>
          {buttons.map((button, index) => (
            <Button
              key={index}
              onClick={button.onClick}
              variant={button.variant || "outlined"}
              color={button.color || "primary"}
              disabled={button.disabled || loading || button.loading}
              startIcon={button.startIcon}
              endIcon={button.endIcon}
              className="px-6 py-2 rounded-full"
              sx={button.sx}
            >
              {button.loading ? <CircularProgress size={20} /> : button.label}
            </Button>
          ))}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CommonPopup;
